import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaTimes,
  FaCheck,
} from "react-icons/fa";
import { TAGS, USERS } from "../config/apiConfig";
import axios from "../config/axiosInstance";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getQueryTagIds = (q) => {
  const fromObjects = (arr, key = "id") =>
    (arr || []).map((o) => toNum(o?.[key])).filter((n) => n !== null);

  if (Array.isArray(q?.Tags)) return fromObjects(q.Tags, "id");
  if (Array.isArray(q?.tags)) return fromObjects(q.tags, "id");
  if (Array.isArray(q?.QueryTags)) return fromObjects(q.QueryTags, "TagId");
  if (Array.isArray(q?.tagIds))
    return (q.tagIds || []).map(toNum).filter((n) => n !== null);
  if (Array.isArray(q?.tagsIds))
    return (q.tagsIds || []).map(toNum).filter((n) => n !== null);
  return [];
};

export default function CompareQueriesPicker() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // até 3

  const { isLoading: loadingQueries, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axios.get(USERS.GET_USER_QUERY_PERMISSIONS);
      return items.data;
    }
  );

  const { isLoading: loadingTags, data: tagsPayload } = useQuery(
    ["credit-tags"],
    async () => {
      const { data } = await axios.get(TAGS.GET_ALL_SIMPLE);
      return Array.isArray(data?.data) ? data.data : data;
    },
    { staleTime: 1000 * 60 * 10 }
  );

  const allTags = useMemo(() => tagsPayload || [], [tagsPayload]);

  const matchesSearch = (query, term) => {
    if (!term) return true;
    const t = normalize(term);
    return normalize(query?.name).includes(t);
  };

  const matchesTagFilterOR = (query, selectedTagIds) => {
    if (!selectedTagIds.length) return true;
    const qTags = getQueryTagIds(query);
    return selectedTagIds.some((id) => qTags.includes(id));
  };

  const filteredQueries = useMemo(() => {
    return (userQueryPermissions || [])
      .filter((q) => {
        const isAdditional =
          typeof q.isAdditional === "boolean"
            ? q.isAdditional
            : Number(q.isAdditional) === 1;
        return q.categoryId === 1 && q.status != -1 && !isAdditional;
      })
      .filter((q) => matchesSearch(q, searchTerm))
      .filter((q) => matchesTagFilterOR(q, selectedTagIds))
      .sort((a, b) => a?.name?.localeCompare(b.name));
  }, [userQueryPermissions, searchTerm, selectedTagIds]);

  const tagCounts = useMemo(() => {
    const counts = new Map();
    (userQueryPermissions || []).forEach((q) => {
      getQueryTagIds(q).forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  }, [userQueryPermissions]);

  const filteredTags = useMemo(() => {
    const s = tagSearch.trim().toLowerCase();
    if (!s) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(s));
  }, [allTags, tagSearch]);

  const toggleTag = (id) => {
    const nid = Number(id);
    setSelectedTagIds((prev) =>
      prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]
    );
  };

  const clearAllTags = () => setSelectedTagIds([]);

  const toggleSelect = (id) => {
    const nid = Number(id);
    setSelectedIds((prev) => {
      const exists = prev.includes(nid);
      if (exists) return prev.filter((x) => x !== nid);
      if (prev.length >= 3) return prev; // trava em 3
      return [...prev, nid];
    });
  };

  const selectedQueries = useMemo(() => {
    const set = new Set(selectedIds);
    // usa todas as consultas disponíveis (e não só as filtradas),
    // para não sumir com as já selecionadas quando mudar o filtro
    return (userQueryPermissions || [])
      .filter((q) => {
        const isAdditional =
          typeof q.isAdditional === "boolean"
            ? q.isAdditional
            : Number(q.isAdditional) === 1;
        return q.categoryId === 1 && q.status != -1 && !isAdditional;
      })
      .filter((q) => set.has(q.id))
      .sort((a, b) => a?.name?.localeCompare(b.name));
  }, [selectedIds, userQueryPermissions]);

  const goToCompare = () => {
    if (selectedQueries.length < 2) return;
    navigate("/queries/compare", {
      state: {
        queries: selectedQueries.slice(0, 3),
        allTags,
      },
    });
  };

  if (loadingQueries || loadingTags) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 w-48 bg-gray-200 rounded" />
          <div className="h-10 w-full bg-gray-200 rounded" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-2 md:px-10 pt-4 pb-24">
      {" "}
      {/* padding-bottom maior por causa da barra fixa */}
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Selecione as Consultas
          </h1>
          <div className="w-20 h-[3px] bg-gradient-to-r from-blue-600 to-green-400 mt-2 rounded" />
          <p className="text-sm text-gray-600 mt-2">
            Escolha <strong>até 3</strong> consultas para comparar os campos
            retornados.
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
        >
          <FaArrowLeft /> Voltar
        </button>
      </div>
      {/* Filtros */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative w-full md:max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar consulta por nome..."
              className="w-full pl-8 pr-10 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/20"
            />
            {!!searchTerm || selectedTagIds.length ? (
              <button
                onClick={() => {
                  setSearchTerm("");
                  clearAllTags();
                  setTagSearch("");
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 text-gray-500"
                title="Limpar filtros"
              >
                <FaTimes />
              </button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setIsTagFilterOpen((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition whitespace-nowrap
              ${
                isTagFilterOpen
                  ? "bg-gradient-to-r from-blue-600 to-green-400 text-white border-transparent shadow"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
          >
            <FaFilter />
            Filtrar por tags
          </button>
        </div>

        {/* Chips de tags ativas */}
        {!!selectedTagIds.length && (
          <div className="mt-3 flex flex-wrap  gap-2">
            {selectedTagIds.map((id) => {
              const tag = allTags.find((t) => t.id === id);
              if (!tag) return null;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {tag.name}
                  <button
                    onClick={() => toggleTag(id)}
                    className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-blue-100"
                    title="Remover"
                  >
                    <FaTimes className="scale-90" />
                  </button>
                </span>
              );
            })}
            <button
              onClick={clearAllTags}
              className="text-xs text-gray-600 underline decoration-dotted hover:text-gray-800"
            >
              Limpar todas
            </button>
          </div>
        )}

        {/* Painel de tags */}
        {isTagFilterOpen && (
          <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-2">
              <div className="relative w-full sm:max-w-xs">
                <FaSearch
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                  size={12}
                />
                <input
                  type="text"
                  placeholder="Buscar tag..."
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-300 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                />
              </div>
              <button
                onClick={() => {
                  clearAllTags();
                  setTagSearch("");
                }}
                className="px-2 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-white"
              >
                Limpar
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {(filteredTags || []).map((tag) => {
                const active = selectedTagIds.includes(tag.id);
                const count = tagCounts.get(tag.id) || 0;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`w-full rounded-lg border px-2.5 py-2 text-left text-[12px] transition-all
                      ${
                        active
                          ? "bg-white text-blue-700 border-blue-600 shadow"
                          : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"
                      }`}
                    title={`${tag.name} • ${count} consulta(s)`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{tag.name}</span>
                      <span
                        className={`shrink-0 inline-flex items-center justify-center h-4 min-w-[18px] px-1.5 text-[10px] rounded-full
                        ${
                          active
                            ? "bg-blue-50 text-blue-700 border border-blue-600/40"
                            : "bg-gray-50 text-gray-600 border border-gray-200"
                        }`}
                      >
                        {count}
                      </span>
                    </div>
                  </button>
                );
              })}
              {!filteredTags?.length && (
                <div className="col-span-full text-xs text-gray-500 py-4 text-center">
                  Nenhuma tag encontrada.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Lista / Grid de consultas */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-700">
            {filteredQueries?.length || 0} consulta
            {filteredQueries?.length === 1 ? "" : "s"} disponível
            {searchTerm || selectedTagIds.length ? " (com filtros)" : ""}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
          {(filteredQueries || []).map((q) => {
            const checked = selectedIds.includes(q.id);
            const fieldsCount = getQueryTagIds(q).length;

            return (
              <label
                key={q.id}
                className={`relative group rounded-2xl border p-4 cursor-pointer transition-all
                  ${
                    checked
                      ? "border-blue-600 ring-2 ring-blue-600/30 bg-blue-50"
                      : "border-gray-200 bg-white hover:shadow-sm hover:-translate-y-[1px]"
                  }`}
              >
                {/* Checkbox acessível */}
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSelect(q.id)}
                  className="sr-only"
                />

                {/* Check visual */}
                <div
                  className={`absolute right-3 top-3 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                    checked
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "border-gray-300 bg-white text-transparent group-hover:text-gray-300"
                  }`}
                >
                  <FaCheck className="text-[11px]" />
                </div>

                <div className="flex flex-col gap-1 pr-8">
                  <div
                    className="text-sm font-semibold text-gray-900 truncate"
                    title={q.name}
                  >
                    {q.name}
                  </div>
                  <div className="text-xs text-gray-600">
                    <span className="inline-flex items-center px-2 py-[2px] rounded-full border text-[11px] bg-gray-50 text-gray-700 border-gray-200">
                      {fieldsCount} campo{fieldsCount === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </label>
            );
          })}

          {!filteredQueries?.length && (
            <div className="col-span-full text-center text-sm text-gray-500 py-12">
              Nenhuma consulta encontrada com os filtros atuais.
            </div>
          )}
        </div>
      </div>
      {/* Barra fixa inferior com seleção */}
      <div className="mt-10">
        <div className="mx-auto pb-3">
          <div className="rounded-2xl border border-gray-200 bg-white/95 backdrop-blur shadow-lg p-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-700">
                Selecionadas: <strong>{selectedIds.length}</strong>/3
              </span>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                  {selectedQueries.map((q) => (
                    <span
                      key={q.id}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                      title={q.name}
                    >
                      <span className="truncate max-w-[40vw] md:max-w-[240px]">
                        {q.name}
                      </span>
                      <button
                        onClick={() => toggleSelect(q.id)}
                        className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-blue-100"
                        title="Remover"
                      >
                        <FaTimes className="scale-90" />
                      </button>
                    </span>
                  ))}
                  {!selectedQueries.length && (
                    <span className="text-xs text-gray-500">
                      Nenhuma selecionada ainda.
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setSelectedIds([]);
                  }}
                  disabled={!selectedIds.length}
                  className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Limpar seleção
                </button>
                <button
                  onClick={goToCompare}
                  disabled={selectedIds.length < 2}
                  className="px-4 py-2 rounded-lg text-sm border border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-600 to-green-400 text-white shadow"
                  title={
                    selectedIds.length < 2
                      ? "Selecione ao menos 2 consultas"
                      : "Comparar agora"
                  }
                >
                  Comparar ({selectedIds.length}/3)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- UI helpers ---- */

function ProgressHint({ value, maxHint }) {
  const max = Math.max(maxHint || 1, 1);
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="mt-3 h-1.5 w-full rounded-full bg-gray-100">
      <div
        className="h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-green-400 transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
